import { createContext, useState, useCallback, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// List of routes that don't require authentication
const PUBLIC_PATHS = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password'
];

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false); // Changed to false by default
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current path is public
  const isPublicPath = useCallback(() => {
    return PUBLIC_PATHS.some(path => location.pathname.startsWith(path));
  }, [location.pathname]);

  // Check auth status - only used for protected routes
  const checkAuth = useCallback(async () => {
    if (!isPublicPath()) {
      try {
        const response = await api.get('/auth/me');
        if (response?.data?.status === 'success') {
          setUser(response.data.data.user);
          return true;
        }
        return false;
      } catch (err) {
        console.error('Auth check failed:', err);
        return false;
      }
    }
    return false;
  }, [isPublicPath]);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      // Skip auth check for public paths
      if (isPublicPath()) {
        setIsInitialized(true);
        return;
      }

      try {
        setLoading(true);
        await checkAuth();
      } catch (err) {
        console.error('Auth initialization failed:', err);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, [checkAuth, isPublicPath]);

  // Handle tab synchronization
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'auth_logout') {
        setUser(null);
        if (!isPublicPath()) {
          navigate('/login');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate, isPublicPath]);

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/auth/login', { email, password });

      if (response?.data?.status !== 'success') {
        throw new Error(response?.data?.message || 'Login failed');
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      setUser(response.data.user);

      const roleRoutes = {
        admin: '/admin/dashboard',
        counselor: '/counselor/dashboard',
        student: '/student/dashboard',
        receptionist: '/receptionist/dashboard',
        hod: '/hod/dashboard',
        staff: '/staff/dashboard'
      };

      const targetRoute = roleRoutes[response.data.user.role];
      if (!targetRoute) {
        throw new Error(`Invalid user role: ${response.data.user.role}`);
      }

      navigate(targetRoute);
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/auth/register', userData);

      if (response?.data?.status !== 'success') {
        throw new Error('Registration failed');
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      setUser(response.data.user);

      const roleRoutes = {
        admin: '/admin/dashboard',
        counselor: '/counselor/dashboard',
        student: '/student/dashboard',
        receptionist: '/receptionist/dashboard',
        hod: '/hod/dashboard',
        staff: '/staff/dashboard',
      };

      const targetRoute = roleRoutes[response.data.user.role];
      if (!targetRoute) {
        throw new Error(`Invalid user role: ${response.data.user.role}`);
      }

      navigate(targetRoute);
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
      delete api.defaults.headers.common['Authorization'];
      localStorage.setItem('auth_logout', Date.now().toString());
      navigate('/login');
    }
  }, [navigate]);

  const updateProfile = useCallback(async (profileData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.put('/auth/profile', profileData);

      if (response?.data?.status !== 'success') {
        throw new Error('Profile update failed');
      }

      setUser(response.data.data.user);
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Profile update failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.put('/auth/change-password', {
        currentPassword,
        newPassword
      });

      if (response?.data?.status !== 'success') {
        throw new Error('Password change failed');
      }

      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Password change failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/auth/forgot-password', { email });

      if (response?.data?.status !== 'success') {
        throw new Error(response?.data?.message || 'Failed to send reset email');
      }

      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to send reset email';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);


  const resetPassword = useCallback(async (resetToken, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.put(`/auth/reset-password/${resetToken}`, { password });

      if (response?.data?.status !== 'success') {
        throw new Error(response?.data?.message || 'Failed to reset password');
      }

      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reset password';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [])

  const contextValue = {
    user,
    loading,
    error,
    isInitialized,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    setError,
    checkAuth
  };

  // Only show loading spinner on protected routes
  if (!isInitialized && loading && !isPublicPath()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-uzima-maroon-400"></div>
      </div>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export { AuthProvider, useAuth, AuthContext };